/**
 * Device Fingerprint Signal Collectors
 *
 * Collects only industry-standard signals used for fraud prevention.
 * All signals use publicly available browser APIs, require no
 * permissions, and work across all modern browsers and mobile devices.
 */

import type { BrowserSignals, HardwareSignals, TimezoneSignals } from './types';

/** Get platform using modern API with fallback */
const getPlatform = (): string => {
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  return uaData?.platform ?? '';
};

/** Collect browser and environment signals */
export const collectBrowserSignals = (): BrowserSignals => ({
  userAgent: navigator.userAgent ?? '',
  platform: getPlatform(),
  language: navigator.language ?? '',
  languages: [...(navigator.languages ?? [])],
  cookieEnabled: navigator.cookieEnabled ?? false,
  doNotTrack: navigator.doNotTrack ?? null,
  webdriver: navigator.webdriver ?? false
});

/** Collect display and hardware signals */
export const collectHardwareSignals = (): HardwareSignals => ({
  screenResolution: [screen.width ?? 0, screen.height ?? 0],
  screenColorDepth: screen.colorDepth ?? 0,
  devicePixelRatio: window.devicePixelRatio ?? 1,
  hardwareConcurrency: navigator.hardwareConcurrency ?? 0,
  deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
  maxTouchPoints: navigator.maxTouchPoints ?? 0
});

/** Collect timezone and locale signals */
export const collectTimezoneSignals = (): TimezoneSignals => {
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = '';
  }

  return {
    timezone,
    timezoneOffset: new Date().getTimezoneOffset()
  };
};

/**
 * Generate a canvas fingerprint.
 *
 * How it works: Renders text and shapes to an off-screen canvas. Different
 * GPU/rendering engines produce subtly different pixel outputs, making this
 * a high-entropy device signal.
 *
 * - No permissions required (standard Canvas 2D API)
 * - Works on all browsers (Chrome, Safari, Firefox, Edge) and mobile
 * - Used by Stripe Radar, Sift, Forter, and all major fraud platforms
 */
export const collectCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Text rendering — font rendering differs across OS/GPU
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(75, 1, 100, 20);
    ctx.fillStyle = '#069';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint:canvas', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.font = '18px Times New Roman';
    ctx.fillText('fingerprint:canvas', 4, 45);

    // Geometric shapes — anti-aliasing and blending differ across renderers
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = 'rgb(255,0,255)';
    ctx.beginPath();
    ctx.arc(50, 50, 25, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgb(0,255,255)';
    ctx.beginPath();
    ctx.arc(80, 50, 25, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    return canvas.toDataURL();
  } catch {
    return '';
  }
};
